// Действия и переходы партии: единая точка действий (движок или сервер), ответы на запросы, новая партия,
// подготовка, онлайн-подключение, полноэкран/звук.
import type { Action, BossId, BotLevel, BotStyle, CardRef, Game as GameState, MutatorId, PlayerId, PromptAnswer } from '../types.ts';
import { BOSS_IDS } from '../engine/bosses.ts';
import { createGame, card, player, turnOf, applyRebalance, setupDone } from '../engine/state.ts';
import { rv } from '../engine/rules.ts';
import { makeRng, shuffle } from '../engine/rng.ts';
import { reviewGame } from '../engine/review.ts';
import { applyAction, chipsPayable, gameFlags, promptWho } from '../engine/game.ts';
import { RULES } from '../engine/rules.ts';
import * as Bot from '../engine/bot.ts';
import { NetClient } from './net.ts';
import type { SavedSession } from './net.ts';
import { loadCorr } from './corr.ts';
import { affordInfo, cnt } from './board.ts';
import { sfx, unlock, isMuted, setMuted } from './sound.ts';
import { freshSetup } from './state.ts';
import { inThirdPhase, setupReady, setupStep, connectingV } from './screens.ts';
import * as OV from './overlays.ts';
import { inputValue } from './dom.ts';
import { st, ui, requireG, viewerId, toast, save, paint, openModal } from './store.ts';
import { render } from './render.ts';
import { botSetupStep, nextSetupPid } from './bots.ts';

/** Единая точка действий: хотсит — локальный движок, онлайн — сервер. Неуспех локального действия — сообщение. */
export function act(a: Action): void {
  if (ui.replay) return; // плеер записи: стол только для просмотра
  if (st.net) {
    st.net.act(a);
    return;
  }
  const g = requireG();
  const r = applyAction(g, viewerId(), a);
  if (!r.ok) {
    sfx('error');
    toast('⚠ ' + r.error);
  }
  resolvePendingTarget();
  save();
  render();
}
export const answer = (v: PromptAnswer): void => {
  ui.selected = new Set();
  ui.numVal = null;
  ui.sheetCollapsed = false;
  act({ name: 'answer', args: [v] });
};

/**
 * Цель, выбранная перетаскиванием карты на игрока: как только движок спросит «кого?», отвечаем сразу.
 * Промежуточные вопросы (например, «+2 мощи или сыграть карту врага?» у шальной магии) цель не сбрасывают;
 * она пропадает, когда эффект карты завершился.
 */
export function resolvePendingTarget(): void {
  const pt = ui.pendingTarget;
  const g = st.G;
  if (!pt || !g) return;
  const pr = g.prompt;
  if (!pr && g.promptFor == null) {
    ui.pendingTarget = null;
    return;
  }
  if (pr && pr.type === 'choosePlayer' && promptWho(g) === ui.viewer && pr.candidates.includes(pt.pid)) {
    ui.pendingTarget = null;
    const a: Action = { name: 'answer', args: [pt.pid] };
    if (st.net) st.net.act(a);
    else applyAction(g, viewerId(), a);
  }
}

/** Что можно купить на оставшуюся мощь (для предупреждения при конце хода) */
export function affordableBuys(g: GameState): string[] {
  const turn = turnOf(g);
  const active = player(g, turn.player);
  const res: string[] = [];
  for (const c of [...g.market, ...g.legendMarket, ...active.familiars]) {
    const { price, afford } = affordInfo(g, active, c);
    if (afford) res.push(`«${card(c).name}» за ${price}`);
  }
  if (cnt(g.chaosPile, g.chaosPileCount) > 0 && turn.power >= RULES.chaosPrice) res.push(`шальную магию за ${RULES.chaosPrice}`);
  return res;
}

/** Купить карту, доплатив чипсинами ровно столько, сколько не хватает мощи */
export function buyWithChips(c: CardRef): void {
  const g = requireG();
  const active = player(g, turnOf(g).player);
  const { needChips } = affordInfo(g, active, c);
  act({ name: 'buyCard', args: [c.iid, chipsPayable(active, c) ? needChips : 0] });
}

// ---------- новая партия и подготовка (хотсит) ----------
/** Состав новой партии хотсита: число мест, боты по местам (null — человек), имена */
export interface TableConfig {
  n: number;
  bots: readonly (BotLevel | null)[];
  names: readonly string[];
  /** босс на последнем месте бота: случайный (от сида партии) или заданный */
  boss?: 'random' | BossId;
  /** мутаторы партии вместо выбранных на экране (дуэль — без них) */
  mutators?: readonly MutatorId[];
}

/** Дуэль: ты и выбранный босс один на один (правила дуэли включатся сами — стол «человек + один босс») */
export function duelTable(boss: BossId): TableConfig {
  return { n: 2, bots: [null, 'hard'], names: [ui.onlineName || 'Ты'], boss, mutators: [] };
}

/** Партия «Против ботов»: ты и выбранное число ботов одного уровня */
export function quickBotsTable(): TableConfig {
  return { n: ui.quickBots + 1, bots: [null, ...Array.from({ length: ui.quickBots }, () => ui.quickLevel)], names: [ui.onlineName || 'Ты'], ...(ui.quickBoss ? { boss: 'random' as const } : {}) };
}

/** Стол, заданный извне (сид дня): сид, мутаторы, характеры ботов по местам, метка дня; режим ребаланса выключен */
export interface TableOverride {
  seed: number;
  mutators: readonly MutatorId[];
  /** характеры ботов в порядке их мест (null — человек) */
  styles: readonly (BotStyle | null)[];
  /** боссы по местам (null — обычный бот или человек) */
  bosses?: readonly (BossId | null)[];
  daily: string;
}

export function startNewGame(table: TableConfig = { n: ui.nPlayers, bots: ui.bots, names: ui.names }, over?: TableOverride): void {
  const seed = over?.seed ?? (Date.now() ^ (Math.random() * 1e9)) >>> 0;
  // ботам раздаются характеры (накладки весов STYLES): без повторов, пока хватает, порядок — от сида партии;
  // бот без своего имени зовётся характером, чтобы за столом были разные узнаваемые соперники
  const styles = shuffle(makeRng(seed), Object.keys(Bot.STYLES) as BotStyle[]);
  // босс: по месту из override (сид дня) или на последнем месте бота («Против ботов» с боссом), случайный — от сида
  const lastBot = table.bots.reduce((acc, b, i) => (b ? i : acc), -1);
  const randomBoss = shuffle(makeRng(seed ^ 0x5eed), [...BOSS_IDS])[0] ?? null;
  const bossAt = (i: number): BossId | null => over ? over.bosses?.[i] ?? null : table.boss && i === lastBot ? (table.boss === 'random' ? randomBoss : table.boss) : null;
  let nBots = 0;
  const players = Array.from({ length: table.n }, (_, i) => {
    const level = table.bots[i] ?? null;
    const boss = bossAt(i);
    if (boss) return { boss, bot: true as const };
    const style = level ? over?.styles[i] ?? styles[nBots++ % styles.length] : undefined;
    const stl = style ? Bot.STYLES[style] : null;
    const botName = stl ? `${stl.emoji} ${stl.label[0]?.toUpperCase() ?? ''}${stl.label.slice(1)}${nBots > styles.length ? ` ${nBots}` : ''}` : `Бот ${i + 1}`;
    return { name: table.names[i] || (level ? botName : `Игрок ${i + 1}`), bot: !!level, ...(level ? { botLevel: level } : {}), ...(style ? { botStyle: style } : {}) };
  });
  // ты и ровно один босс — дуэль: её правила (отстающий берёт карту) включаются сами
  const duel = table.n === 2 && players.filter((p) => !p.bot).length === 1 && players.some((p) => 'boss' in p && !!p.boss);
  const mutators: MutatorId[] = over ? [...over.mutators] : [...(table.mutators ?? ui.mutators), ...(duel ? ['duel' as const] : [])];
  const g = createGame({ seed, players, ...(mutators.length ? { mutators } : {}), ...(!over && ui.rebalance ? { rebalance: true } : {}) });
  if (over) g.daily = over.daily;
  st.G = g;
  // боты выбирают первыми: отвергнутые ими фамильяры становятся ничейными и доступны людям («три фамильяра»)
  for (const p of g.players) {
    if (!p.bot) continue;
    botSetupStep(g, p.id);
  }
  ui.screen = 'setup';
  // один человек за столом — телефон передавать некому, выбор открывается сразу
  const humans = players.filter((p) => !p.bot).length;
  ui.setup = freshSetup(nextSetupPid(g, -1) ?? 0, humans === 1 ? 'choose' : 'pass');
  ui.viewer = null;
  ui.seenSeq = {};
  ui.lastSeq = 0;
  if (g.phase !== 'setup') ui.screen = 'game';
  // сохранить сразу: партия, закрытая посреди подготовки, продолжается с главного экрана
  save();
  render();
}

/** Следующий игрок в подготовке (после последнего — вторая фаза, третьи фамильяры); когда всё выбрано, партия уже началась */
export function advanceSetup(g: GameState, pid: PlayerId): void {
  // в фазе третьего фамильяра порядок задаёт движок (setup.third), а не места за столом
  const next = inThirdPhase(g, pid) || g.players.every(setupDone) ? g.setup?.third?.[0] ?? null : nextSetupPid(g, pid);
  if (next != null && g.phase === 'setup') ui.setup = freshSetup(next, 'pass');
  else {
    ui.screen = 'game';
    ui.viewer = null;
    ui.lastSeq = 0;
  }
}

export function setupDoneForPlayer(): void {
  const g = requireG();
  const s = ui.setup;
  let a: Action;
  const step = setupStep(g, s.pid);
  if (inThirdPhase(g, s.pid)) a = { name: 'setupThird', args: [s.freeFam] };
  else if (!setupReady(g, s)) return;
  else if (step === 'familiar' && s.familiar) a = { name: 'setupFamiliar', args: [s.familiar] };
  else {
    if (!s.traitId) return;
    // с v11 жетон уходит отдельным шагом, фамильяры откроются после него
    a = { name: 'setupChoose', args: [{ traitId: s.traitId, familiar: step === 'both' ? s.familiar : null }] };
  }
  if (st.net) {
    st.net.act(a);
    return;
  }
  const r = applyAction(g, s.pid, a);
  if (!r.ok) {
    toast('⚠ ' + r.error);
    return;
  }
  // жетон выбран, а фамильяр ещё нет — тот же игрок выбирает дальше, телефон не передаётся
  if (!inThirdPhase(g, s.pid) && !setupDone(player(g, s.pid))) {
    s.familiar = null;
    save();
    render();
    return;
  }
  advanceSetup(g, s.pid);
  save();
  render();
}

/** Послематчевый разбор: реплей журнала + сравнение решений людей с ботом (движковый reviewGame, ~секунда) */
export function showReview(): void {
  const g = st.G;
  if (st.net || !g || !g.journal?.length || g.seed == null) return;
  const humans = g.players.filter((p) => !p.bot).map((p) => p.id);
  if (!humans.length) return;
  const rec = {
    seed: g.seed,
    players: g.players.map((p) => ({ name: p.name, bot: p.bot, ...(p.botLevel ? { botLevel: p.botLevel } : {}), ...(p.botStyle ? { botStyle: p.botStyle } : {}) })),
    ...gameFlags(g),
    journal: g.journal,
  };
  const items = reviewGame(rec, humans);
  openModal(() => OV.reviewV(g, items));
}

// ---------- онлайн ----------
/**
 * Онлайн-комната: создать, войти по коду, вернуться в запомненную или открыть партию по переписке (corr: место из
 * списка партий). opts.size — создать партию по переписке на столько мест; opts.code — войти в эту комнату.
 */
export function startOnline(kind: 'create' | 'join' | 'resume' | 'corr', opts: { size?: number; code?: string; session?: SavedSession } = {}): void {
  // имя — из поля стартового экрана, а если его на экране нет (вход из приглашения посреди хотсита) — запомненное
  const name = (inputValue('online-name') || ui.onlineName).trim() || 'Игрок';
  ui.onlineName = name;
  try { localStorage.setItem('krutagidon-name', name); } catch { /* приватный режим */ }
  const client = new NetClient({
    onView: (m) => {
      const first = !st.G;
      const g = m.G;
      // цены и тексты карт — по режиму и версии правил партии (представление их несёт; клиент createGame не звал)
      if (g) applyRebalance(!!g.rebalance, rv(g));
      st.G = g;
      ui.viewer = m.me;
      if (g && g.phase === 'setup' && (ui.setup.pid !== m.me || !ui.setup.online)) ui.setup = freshSetup(m.me, 'choose', true);
      // вторая фаза: свежий выбор третьего фамильяра
      if (g && g.phase === 'setup' && inThirdPhase(g, m.me) && ui.setup.traitId) ui.setup = freshSetup(m.me, 'choose', true);
      if (g && (first || ui.lastSeq === 0)) ui.lastSeq = g.eventSeq;
      resolvePendingTarget();
      render();
    },
    onError: (e) => {
      sfx('error');
      toast('⚠ ' + e, 3500);
    },
    onStatus: (s) => {
      ui.netStatus = s;
      if (s === 'disconnected') toast('Нет связи с сервером, переподключаюсь…');
      if (st.net && !st.G) render();
    },
  });
  st.net = client;
  let p: Promise<void>;
  if (kind === 'create') p = client.create(name, opts.size ?? 0);
  else if (kind === 'join') p = client.join((opts.code ?? (inputValue('join-code') || ui.joinCode)).trim().toUpperCase(), name);
  else if (kind === 'corr' && opts.session) {
    client.corr = true;
    client.resume(opts.session);
    p = Promise.resolve();
  } else {
    const saved = NetClient.saved();
    if (!saved) {
      st.net = null;
      toast('Нет сохранённой комнаты');
      render();
      return;
    }
    client.resume(saved);
    p = Promise.resolve();
  }
  paint(connectingV());
  p.catch((e: unknown) => {
    toast('⚠ ' + (e instanceof Error ? e.message : String(e)), 4000);
    st.net = null;
    render();
  });
}

export function leaveOnline(): void {
  const corr = !!st.net?.corr;
  if (st.net) st.net.leave();
  st.net = null;
  st.G = null;
  ui.screen = 'start';
  ui.lastSeq = 0;
  // из партии по переписке — к списку партий (там видно, где ждут хода)
  if (corr) {
    ui.home = 'corr';
    void loadCorr(render);
  }
  render();
}

// ---------- полноэкран и звук ----------
// Полный экран (на iOS Safari API нет — кнопка скрыта стилем)
export function toggleFullscreen(): void {
  const el = document.documentElement;
  try {
    if (document.fullscreenElement) void document.exitFullscreen();
    else if (el.requestFullscreen) el.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
  } catch {
    /* ignore */
  }
}
document.addEventListener('fullscreenchange', () => { document.body.classList.toggle('fs', !!document.fullscreenElement); });
if (!document.documentElement.requestFullscreen) document.documentElement.classList.add('no-fs');

export function toggleMute(): void {
  setMuted(!isMuted());
  if (!isMuted()) {
    unlock();
    sfx('select');
  }
}
